/**
 * Control-group registry for Feedback location dropdowns.
 *
 * When adding a new high-level UI control group, call registerControlGroup with a
 * stable id and human-readable path monickers (highest → lowest). Labels join with " - ".
 *
 * Keep in sync with backend control-group-registry.js.
 */

export type ControlGroupDef = {
	id: string;
	path: string[];
	featureOnly?: boolean;
};

export type ControlGroupEntry = {
	id: string;
	path: string[];
	label: string;
	featureOnly: boolean;
};

const entries: ControlGroupEntry[] = [];

export function registerControlGroup(def: ControlGroupDef): ControlGroupEntry {
	const id = String(def.id || '').trim();
	const path = Array.isArray(def.path)
		? def.path.map((p) => String(p || '').trim()).filter(Boolean)
		: [];
	if (!id || path.length === 0) {
		throw new Error('registerControlGroup requires id and non-empty path');
	}
	if (entries.some((e) => e.id === id)) {
		throw new Error(`Control group already registered: ${id}`);
	}
	const entry: ControlGroupEntry = {
		id,
		path,
		label: path.join(' - '),
		featureOnly: Boolean(def.featureOnly),
	};
	entries.push(entry);
	return entry;
}

export function listControlGroups(): ControlGroupEntry[] {
	return entries.slice();
}

export function controlGroupLabels(opts?: { includeFeatureOnly?: boolean }): string[] {
	const includeFeatureOnly = Boolean(opts?.includeFeatureOnly);
	return entries
		.filter((e) => (includeFeatureOnly ? true : !e.featureOnly))
		.map((e) => e.label);
}

function seed() {
	if (entries.length) return;

	registerControlGroup({ id: 'account', path: ['Account'] });
	registerControlGroup({ id: 'account-ecosystem-settings', path: ['Account', 'Ecosystem Settings'] });
	registerControlGroup({ id: 'account-testing-runtime', path: ['Account', 'Testing runtime'] });
	registerControlGroup({ id: 'account-logout', path: ['Account', 'Logout'] });

	registerControlGroup({ id: 'dashboard-nav', path: ['Dashboard Navigation'] });
	registerControlGroup({ id: 'dashboard-nav-home', path: ['Dashboard Navigation', 'Home'] });
	registerControlGroup({
		id: 'dashboard-nav-system-admin',
		path: ['Dashboard Navigation', 'System Admin'],
	});
	registerControlGroup({
		id: 'dashboard-nav-realm-admin',
		path: ['Dashboard Navigation', 'Realm Admin'],
	});
	registerControlGroup({
		id: 'dashboard-nav-ecosystem-admin',
		path: ['Dashboard Navigation', 'Ecosystem Admin'],
	});
	registerControlGroup({
		id: 'dashboard-nav-project-admin',
		path: ['Dashboard Navigation', 'Project Admin'],
	});

	registerControlGroup({ id: 'service-nav', path: ['Service Navigation'] });
	registerControlGroup({ id: 'service-nav-openproject', path: ['Service Navigation', 'OpenProject'] });
	registerControlGroup({ id: 'service-nav-gitea', path: ['Service Navigation', 'Gitea'] });
	registerControlGroup({ id: 'service-nav-nextcloud', path: ['Service Navigation', 'Nextcloud'] });
	registerControlGroup({ id: 'service-nav-collabora', path: ['Service Navigation', 'Collabora'] });
	registerControlGroup({ id: 'service-nav-vault', path: ['Service Navigation', 'Vault'] });
	registerControlGroup({ id: 'service-nav-ci', path: ['Service Navigation', 'CI / Woodpecker'] });

	registerControlGroup({
		id: 'project-tracking',
		path: ['Project Tracking and Management'],
	});
	registerControlGroup({
		id: 'project-tracking-ecosystem',
		path: ['Project Tracking and Management', 'Ecosystem'],
	});
	registerControlGroup({
		id: 'project-tracking-project',
		path: ['Project Tracking and Management', 'Project'],
	});
	registerControlGroup({
		id: 'project-tracking-widgets',
		path: ['Project Tracking and Management', 'Project', 'Widgets'],
	});
	registerControlGroup({
		id: 'project-tracking-ai-settings',
		path: ['Project Tracking and Management', 'Project', 'AI settings'],
	});

	registerControlGroup({
		id: 'bug-feature-report',
		path: ['Bug report / Feature request'],
	});

	registerControlGroup({ id: 'auth-login', path: ['Auth / login'] });
	registerControlGroup({ id: 'email-notifications', path: ['Email / notifications'] });
	registerControlGroup({ id: 'keycloak-platform', path: ['Keycloak / Auth (platform)'] });

	registerControlGroup({ id: 'other', path: ['Other'] });
	registerControlGroup({
		id: 'not-applicable',
		path: ['Not applicable'],
		featureOnly: true,
	});
	registerControlGroup({
		id: 'new-area',
		path: ['New area / not listed'],
		featureOnly: true,
	});
}

seed();
